// Puzzle runner: holds the active puzzle (if any), stamps its
// starter cells into the sandbox when one starts, polls the goal
// predicate every ~250 ms, and persists completion to
// localStorage (DB sync is a follow-up).
//
// The runner deliberately doesn't clear the sandbox on start; the
// puzzles' coordinate anchors (around (20..120, 20..120)) sit in a
// region the canvas can paint over without trashing whatever the
// user had going on. App can wire a "clear & start" path through
// `sandbox.clearAll` if it wants the hard reset.

import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "../auth/client";
import { useAuth } from "../auth/AuthProvider";
import type { SandboxLike } from "../sim/types";
import { findPuzzle } from "./data";
import type { Puzzle } from "./types";

const STORAGE_KEY = "coscience.puzzle.completed.v1";
const POLL_INTERVAL_MS = 250;

function readCompleted(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((s): s is string => typeof s === "string"));
  } catch {
    return new Set();
  }
}

function writeCompleted(set: Set<string>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    // QuotaExceeded or disabled storage — silently skip, the in-
    // memory set still tracks the session.
  }
}

export interface PuzzleRunnerState {
  /** Active puzzle, or `null` when nothing is running. */
  active: Puzzle | null;
  /** Flips true the first time the active puzzle's `check` predicate
   * returns true; stays true until the user dismisses or starts
   * another puzzle. */
  done: boolean;
  /** Stable set of all puzzle ids the user has ever completed. */
  completedIds: Set<string>;
  /** Begin a puzzle. Stamps starter cells; the active poll picks up
   * after the next render frame. */
  start: (puzzle: Puzzle) => void;
  /** Dismiss the active puzzle without recording a completion. */
  cancel: () => void;
}

export function usePuzzleRunner(sandbox: SandboxLike | null): PuzzleRunnerState {
  const { user } = useAuth();
  const [active, setActive] = useState<Puzzle | null>(null);
  const [done, setDone] = useState(false);
  const [completedIds, setCompletedIds] = useState<Set<string>>(() => readCompleted());

  // Sign-in sync: every time `user` flips from null to a real
  // account, pull the server's puzzle progress and union it with
  // whatever the user already had in localStorage. Anything the
  // local set knew that the server didn't gets POSTed back so the
  // two stay aligned -- handy when the user was solving puzzles
  // before signing in. Signing out leaves the local set alone.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.listPuzzleProgress();
        if (cancelled) return;
        setCompletedIds((prev) => {
          const merged = new Set(prev);
          for (const row of res.completed) merged.add(row.puzzle_id);
          writeCompleted(merged);
          // Anything in `prev` that the server hasn't recorded yet
          // gets pushed up. Fire and forget; the server is
          // idempotent on `puzzle_id` so a re-POST is a no-op.
          const serverIds = new Set(res.completed.map((row) => row.puzzle_id));
          for (const id of prev) {
            if (!serverIds.has(id)) {
              void api.savePuzzleProgress(id).catch(() => undefined);
            }
          }
          return merged;
        });
      } catch {
        // Network / auth failure -- keep the local set as-is. The
        // local-only path stays functional.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Stamp the starter cells.
  const stamp = useCallback(
    (puzzle: Puzzle) => {
      if (!sandbox) return;
      for (const cell of puzzle.start) {
        const id = sandbox.materialId(cell.material);
        if (id === 0) continue;
        if (cell.state && sandbox.paintCellState) {
          sandbox.paintCellState(cell.x, cell.y, id, cell.state);
        } else {
          sandbox.paintCircle(cell.x, cell.y, 0, id);
        }
      }
    },
    [sandbox],
  );

  const start = useCallback(
    (puzzle: Puzzle) => {
      setActive(puzzle);
      setDone(false);
      // Stamp after we set the active puzzle so the polling effect
      // sees the new puzzle on its next iteration.
      stamp(puzzle);
    },
    [stamp],
  );

  const cancel = useCallback(() => {
    setActive(null);
    setDone(false);
  }, []);

  // Restore the active puzzle's cells whenever the sandbox swaps out
  // (e.g. the user changed world resolution); without this, picking
  // a puzzle and then resizing the world would leave a partial
  // scene the goal predicate can never satisfy.
  const activeRef = useRef(active);
  activeRef.current = active;
  useEffect(() => {
    if (sandbox && activeRef.current) stamp(activeRef.current);
  }, [sandbox, stamp]);

  // Poll the goal predicate while a puzzle is active.
  const userRef = useRef(user);
  userRef.current = user;
  useEffect(() => {
    if (!sandbox || !active || done) return;
    const id = window.setInterval(() => {
      try {
        if (active.check(sandbox)) {
          setDone(true);
          setCompletedIds((prev) => {
            if (prev.has(active.id)) return prev;
            const next = new Set(prev);
            next.add(active.id);
            writeCompleted(next);
            // DB persist when signed in. Fire and forget -- the
            // server is idempotent on (user_id, puzzle_id) so a
            // re-post after a refresh is a silent no-op.
            if (userRef.current) {
              void api.savePuzzleProgress(active.id).catch(() => undefined);
            }
            return next;
          });
        }
      } catch {
        // A predicate that throws (e.g. out-of-bounds during a world
        // resize) is treated as "not yet" — the next tick re-runs it.
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [sandbox, active, done]);

  return { active, done, completedIds, start, cancel };
}

/** Reload the completed-id set from localStorage. Useful when the
 * caller suspects another tab updated it, or after a server-side
 * sync brings in additional ids. */
export function reloadCompleted(): Set<string> {
  return readCompleted();
}

export { findPuzzle };
